import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const TarixceM = () => {
    const [data, setData] = useState([]);
    const [allPeriods, setAllPeriods] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState("");
    const [filteredPeriodSalaries, setFilteredPeriodSalaries] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retrying, setRetrying] = useState(false);

    /**
     * Serverdən gələn xətanı istifadəçiyə mümkün qədər
     * ətraflı və başadüşülən şəkildə göstərir.
     */
    const getErrorMessage = (error, defaultMessage) => {
        if (!error) {
            return defaultMessage;
        }

        const responseData = error.response?.data;

        if (responseData) {
            if (typeof responseData === "string" && responseData.trim()) {
                return responseData;
            }

            if (responseData.message) {
                let message = responseData.message;

                if (responseData.details) {
                    message += `\nƏlavə məlumat: ${responseData.details}`;
                }

                if (responseData.error && responseData.error !== responseData.message) {
                    message += `\nTexniki məlumat: ${responseData.error}`;
                }

                return message;
            }

            if (responseData.error) {
                return `Xəta: ${responseData.error}`;
            }

            if (responseData.details) {
                return `Əlavə məlumat: ${responseData.details}`;
            }

            try {
                const stringified = JSON.stringify(responseData);

                if (stringified && stringified !== "{}") {
                    return `Serverin cavabı: ${stringified}`;
                }
            } catch (stringifyError) {
                console.error("Xəta məlumatı oxunarkən problem yarandı:", stringifyError);
            }
        }

        if (error.response?.status) {
            const status = error.response.status;

            if (status === 401) {
                return "Sessiyanızın müddəti bitib və ya giriş icazəniz yoxdur. Zəhmət olmasa hesabınıza yenidən daxil olun.";
            }

            if (status === 403) {
                return "Bu məlumatlara baxmaq üçün icazəniz yoxdur.";
            }

            if (status === 404) {
                return "Məlumat üçün tələb olunan server ünvanı tapılmadı.";
            }

            if (status === 500) {
                return "Server daxilində xəta baş verdi. Server cavabında əlavə məlumat yoxdursa, bir qədər sonra yenidən cəhd edin.";
            }

            return `Server ${status} kodu ilə cavab verdi.`;
        }

        if (error.request) {
            return "Serverə sorğu göndərildi, lakin cavab alınmadı. İnternet bağlantınızı və serverin işlək vəziyyətdə olduğunu yoxlayın.";
        }

        if (error.message) {
            return `Sorğu zamanı xəta baş verdi: ${error.message}`;
        }

        return defaultMessage;
    };

    /**
     * Maaş məbləğini daha səliqəli göstərir.
     */
    const formatSalary = (value) => {
        const number = Number(value);

        if (!Number.isFinite(number)) {
            return "0.00";
        }

        return number.toLocaleString("az-AZ", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    /**
     * Məlumatların serverdən yüklənməsi.
     */
    const fetchData = async (isRetry = false) => {
        try {
            if (isRetry) {
                setRetrying(true);
            } else {
                setLoading(true);
            }

            setError(null);

            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/vanHistory`,
                {
                    withCredentials: true,
                }
            );

            const responseData = Array.isArray(response.data)
                ? response.data
                : [];

            setData(responseData);

            const all = responseData.flatMap((user) =>
                Array.isArray(user?.periodSalaries)
                    ? user.periodSalaries
                        .map((period) => period?.periodLabel)
                        .filter(Boolean)
                    : []
            );

            const uniquePeriods = [...new Set(all)];

            setAllPeriods(uniquePeriods);

            if (uniquePeriods.length > 0) {
                setSelectedPeriod((previousPeriod) =>
                    previousPeriod && uniquePeriods.includes(previousPeriod)
                        ? previousPeriod
                        : uniquePeriods[0]
                );
            } else {
                setSelectedPeriod("");
            }
        } catch (error) {
            console.error("Tarixçə məlumatları alınarkən xəta:", error);

            setError(
                getErrorMessage(
                    error,
                    "Tarixçə maaş məlumatlarını yükləmək mümkün olmadı."
                )
            );

            setData([]);
            setAllPeriods([]);
            setSelectedPeriod("");
        } finally {
            setLoading(false);
            setRetrying(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    /**
     * Seçilmiş dövrə uyğun maaş məlumatlarını hazırlayır.
     */
    useEffect(() => {
        if (!selectedPeriod || data.length === 0) {
            setFilteredPeriodSalaries([]);
            return;
        }

        const filtered = data
            .map((user) => {
                const periodSalaries = Array.isArray(user?.periodSalaries)
                    ? user.periodSalaries
                    : [];

                const matching = periodSalaries.find(
                    (period) => period?.periodLabel === selectedPeriod
                );

                if (!matching) {
                    return null;
                }

                return {
                    ...matching,
                    totalUserSalary: user?.salary ?? 0,
                };
            })
            .filter(Boolean);

        setFilteredPeriodSalaries(filtered);
    }, [selectedPeriod, data]);

    /**
     * Axtarış nəticələri.
     * Ad, e-poçt və referans kodu üzrə axtarış edir.
     */
    const filteredSalaries = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();

        if (!query) {
            return filteredPeriodSalaries;
        }

        return filteredPeriodSalaries.filter((user) => {
            const name = String(user?.name || "").toLowerCase();
            const email = String(user?.email || "").toLowerCase();
            const referralCode = String(user?.referralCode || "").toLowerCase();

            return (
                name.includes(query) ||
                email.includes(query) ||
                referralCode.includes(query)
            );
        });
    }, [filteredPeriodSalaries, searchQuery]);

    const totalVisibleSalary = useMemo(() => {
        return filteredSalaries.reduce((total, user) => {
            return total + (Number(user?.salary) || 0);
        }, 0);
    }, [filteredSalaries]);

    const totalVisibleUserSalary = useMemo(() => {
        return filteredSalaries.reduce((total, user) => {
            return total + (Number(user?.totalUserSalary) || 0);
        }, 0);
    }, [filteredSalaries]);

    const handleClearSearch = () => {
        setSearchQuery("");
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-100 to-indigo-50 dark:from-gray-950 dark:via-gray-900 dark:to-slate-950 text-gray-900 dark:text-white px-4 sm:px-6 py-8 sm:py-10 transition-colors">
            <div className="max-w-7xl mx-auto space-y-8">

                {/* Başlıq */}
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 p-6 sm:p-8 shadow-2xl">
                    <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-white/10 blur-2xl" />
                    <div className="absolute -left-20 -bottom-20 w-56 h-56 rounded-full bg-white/10 blur-3xl" />

                    <div className="relative">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center text-2xl border border-white/20">
                                🧾
                            </div>

                            <div>
                                <p className="text-indigo-100 text-sm font-medium">
                                    Maaş tarixçəsi
                                </p>

                                <h1 className="text-2xl sm:text-3xl font-extrabold text-white">
                                    Tarixçə Maaş Paneli
                                </h1>
                            </div>
                        </div>

                        <p className="text-indigo-100 text-sm sm:text-base max-w-2xl">
                            Müxtəlif dövrlər üzrə maaş məlumatlarınıza baxın,
                            istifadəçiləri axtarın və maaş göstəricilərini müqayisə edin.
                        </p>
                    </div>
                </div>

                {/* Xəta paneli */}
                {error && (
                    <div className="rounded-3xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 p-5 sm:p-6 shadow-lg">
                        <div className="flex flex-col sm:flex-row gap-4 sm:items-start sm:justify-between">
                            <div className="flex gap-4">
                                <div className="shrink-0 w-11 h-11 rounded-2xl bg-red-100 dark:bg-red-900/40 flex items-center justify-center text-xl">
                                    ⚠️
                                </div>

                                <div>
                                    <h2 className="font-bold text-red-800 dark:text-red-300 text-lg">
                                        Məlumatları yükləmək mümkün olmadı
                                    </h2>

                                    <p className="mt-2 text-sm text-red-700 dark:text-red-300 whitespace-pre-line leading-6">
                                        {error}
                                    </p>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => fetchData(true)}
                                disabled={retrying}
                                className="shrink-0 px-5 py-3 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold transition-all shadow-md"
                            >
                                {retrying ? "Yenidən yoxlanılır..." : "Yenidən cəhd et"}
                            </button>
                        </div>
                    </div>
                )}

                {/* Yüklənmə vəziyyəti */}
                {loading && (
                    <div className="rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 shadow-xl p-8">
                        <div className="flex flex-col items-center justify-center text-center">
                            <div className="w-14 h-14 rounded-full border-4 border-indigo-100 dark:border-gray-700 border-t-indigo-600 animate-spin mb-5" />

                            <h2 className="text-lg font-bold">
                                Maaş məlumatları yüklənir...
                            </h2>

                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                Tarixçə məlumatları serverdən alınır. Zəhmət olmasa gözləyin.
                            </p>
                        </div>
                    </div>
                )}

                {/* Əsas məzmun */}
                {!loading && !error && (
                    <>
                        {/* Məlumat yoxdur */}
                        {data.length === 0 ? (
                            <div className="rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 shadow-xl p-10 text-center">
                                <div className="w-16 h-16 mx-auto rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-3xl mb-5">
                                    📭
                                </div>

                                <h2 className="text-xl font-bold">
                                    Maaş tarixçəsi tapılmadı
                                </h2>

                                <p className="mt-2 text-gray-500 dark:text-gray-400">
                                    Hazırda göstərilə biləcək maaş tarixçəsi məlumatı yoxdur.
                                </p>

                                <button
                                    type="button"
                                    onClick={() => fetchData(true)}
                                    disabled={retrying}
                                    className="mt-6 px-5 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-semibold transition"
                                >
                                    {retrying
                                        ? "Yenidən yüklənir..."
                                        : "Məlumatları yenilə"}
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Dövr və axtarış */}
                                <div className="rounded-3xl bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border border-gray-200 dark:border-white/10 shadow-xl p-5 sm:p-6">
                                    <div className="flex flex-col lg:flex-row gap-5 lg:items-end lg:justify-between">

                                        <div className="w-full lg:w-auto">
                                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                                📅 Maaş dövrünü seçin
                                            </label>

                                            <select
                                                value={selectedPeriod}
                                                onChange={(e) =>
                                                    setSelectedPeriod(e.target.value)
                                                }
                                                disabled={allPeriods.length === 0}
                                                className="w-full lg:min-w-[260px] bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white p-3.5 rounded-2xl shadow-sm outline-none focus:ring-2 focus:ring-indigo-500 transition"
                                            >
                                                {allPeriods.length > 0 ? (
                                                    allPeriods.map((period, i) => (
                                                        <option key={i} value={period}>
                                                            {period}
                                                        </option>
                                                    ))
                                                ) : (
                                                    <option value="">
                                                        Dövr tapılmadı
                                                    </option>
                                                )}
                                            </select>
                                        </div>

                                        <div className="w-full lg:flex-1 lg:max-w-xl">
                                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                                🔍 İstifadəçi axtarışı
                                            </label>

                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder="Ad, e-poçt və ya referans kodu ilə axtar..."
                                                    value={searchQuery}
                                                    onChange={(e) =>
                                                        setSearchQuery(e.target.value)
                                                    }
                                                    className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white p-3.5 pr-12 rounded-2xl shadow-sm outline-none focus:ring-2 focus:ring-indigo-500 transition"
                                                />

                                                {searchQuery && (
                                                    <button
                                                        type="button"
                                                        onClick={handleClearSearch}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-200 transition"
                                                        aria-label="Axtarışı təmizlə"
                                                    >
                                                        ✕
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Filtr məlumatı */}
                                    <div className="mt-5 flex flex-wrap gap-3">
                                        <div className="px-4 py-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 text-sm font-semibold">
                                            Dövr: {selectedPeriod || "Seçilməyib"}
                                        </div>

                                        <div className="px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                                            Göstərilən istifadəçi sayı: {filteredSalaries.length}
                                        </div>
                                    </div>
                                </div>

                                {/* Statistik kartlar */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                    <div className="rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 shadow-lg p-6">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    Seçilmiş dövr üzrə istifadəçi sayı
                                                </p>

                                                <p className="text-3xl font-extrabold mt-2">
                                                    {filteredSalaries.length}
                                                </p>
                                            </div>

                                            <div className="w-12 h-12 rounded-2xl bg-indigo-100 dark:bg-indigo-950/50 flex items-center justify-center text-xl">
                                                👥
                                            </div>
                                        </div>
                                    </div>

                                    <div className="rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 shadow-lg p-6">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    Seçilmiş dövr üzrə maaş
                                                </p>

                                                <p className="text-2xl font-extrabold mt-2 text-yellow-600 dark:text-yellow-400">
                                                    {formatSalary(totalVisibleSalary)} AZN
                                                </p>
                                            </div>

                                            <div className="w-12 h-12 rounded-2xl bg-yellow-100 dark:bg-yellow-950/40 flex items-center justify-center text-xl">
                                                💰
                                            </div>
                                        </div>
                                    </div>

                                    <div className="rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 shadow-lg p-6">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    Ümumi maaş göstəricisi
                                                </p>

                                                <p className="text-2xl font-extrabold mt-2 text-green-600 dark:text-green-400">
                                                    {formatSalary(totalVisibleUserSalary)} AZN
                                                </p>
                                            </div>

                                            <div className="w-12 h-12 rounded-2xl bg-green-100 dark:bg-green-950/40 flex items-center justify-center text-xl">
                                                📈
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Cədvəl */}
                                <div className="rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 shadow-2xl overflow-hidden">
                                    <div className="p-5 sm:p-6 border-b border-gray-200 dark:border-gray-800">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                            <div>
                                                <h2 className="text-xl sm:text-2xl font-extrabold">
                                                    📋 Maaş tarixçəsi
                                                </h2>

                                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                    Seçilmiş dövr üzrə maaş məlumatları
                                                </p>
                                            </div>

                                            {searchQuery && (
                                                <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                                                    Axtarış: "{searchQuery}"
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <div className="max-h-[550px] overflow-y-auto">
                                            <table className="w-full min-w-[950px] text-sm">
                                                <thead className="bg-gray-100/95 dark:bg-gray-800/95 text-gray-700 dark:text-gray-200 sticky top-0 z-10 backdrop-blur-md">
                                                    <tr>
                                                        <th className="p-4 text-center font-bold">
                                                            #
                                                        </th>

                                                        <th className="p-4 text-left font-bold">
                                                            📷 Fotoğraf
                                                        </th>

                                                        <th className="p-4 text-left font-bold">
                                                            👤 Ad
                                                        </th>

                                                        <th className="p-4 text-left font-bold">
                                                            📧 E-poçt
                                                        </th>

                                                        <th className="p-4 text-left font-bold">
                                                            🎖 Rütbə
                                                        </th>

                                                        <th className="p-4 text-left font-bold">
                                                            📆 Dövr üzrə maaş
                                                        </th>

                                                        <th className="p-4 text-left font-bold">
                                                            💰 Ümumi maaş
                                                        </th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {filteredSalaries.map((user, i) => (
                                                        <tr
                                                            key={`${user?.email || user?.name || "user"}-${i}`}
                                                            className="border-b border-gray-200 dark:border-gray-800 hover:bg-indigo-50/60 dark:hover:bg-white/5 transition"
                                                        >
                                                            <td className="p-4 text-center font-bold text-gray-500 dark:text-gray-400">
                                                                {i + 1}
                                                            </td>

                                                            <td className="p-4">
                                                                <div className="w-12 h-12 rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                                                                    <img
                                                                        src={
                                                                            user?.photo ||
                                                                            "https://via.placeholder.com/100?text=Foto"
                                                                        }
                                                                        alt={`${user?.name || "İstifadəçi"} fotoşəkli`}
                                                                        className="w-full h-full object-cover"
                                                                        onError={(e) => {
                                                                            e.currentTarget.src =
                                                                                "https://via.placeholder.com/100?text=Foto";
                                                                        }}
                                                                    />
                                                                </div>
                                                            </td>

                                                            <td className="p-4 font-semibold">
                                                                {user?.name || "Ad göstərilməyib"}
                                                            </td>

                                                            <td className="p-4 text-gray-600 dark:text-gray-300">
                                                                {user?.email || "E-poçt göstərilməyib"}
                                                            </td>

                                                            <td className="p-4">
                                                                <span className="inline-flex px-3 py-1.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-semibold">
                                                                    {user?.rank || "Rütbə göstərilməyib"}
                                                                </span>
                                                            </td>

                                                            <td className="p-4 font-bold text-yellow-600 dark:text-yellow-400">
                                                                {formatSalary(user?.salary)} AZN
                                                            </td>

                                                            <td className="p-4 font-bold text-green-600 dark:text-green-400">
                                                                {formatSalary(user?.totalUserSalary)} AZN
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Nəticə yoxdur */}
                                    {filteredSalaries.length === 0 && (
                                        <div className="p-10 text-center border-t border-gray-200 dark:border-gray-800">
                                            <div className="w-14 h-14 mx-auto rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-2xl mb-4">
                                                🔎
                                            </div>

                                            <h3 className="text-lg font-bold">
                                                Uyğun istifadəçi tapılmadı
                                            </h3>

                                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                                Axtarış sözünü dəyişin və ya başqa maaş dövrü seçin.
                                            </p>

                                            {searchQuery && (
                                                <button
                                                    type="button"
                                                    onClick={handleClearSearch}
                                                    className="mt-5 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition"
                                                >
                                                    Axtarışı təmizlə
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default TarixceM;
